import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography, Autocomplete } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarBalanzas = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Mantenemos la referencia del código original para la edición
  const [balcodigoViejo, setBalcodigoViejo] = useState("")

  const [formData, setFormData] = useState({
    balcodigo: "",
    baldescri: "",
    balip: "",
    balclave: "",
    balimpresora: "",
    loccodigo: "",
    balstatus: "A",
  })

  useEffect(() => {
    if (state) {
      setBalcodigoViejo(state.balcodigo || "")
      setFormData({
        balcodigo: state.balcodigo || "",
        baldescri: state.baldescri || "",
        balip: state.balip || "",
        balclave: state.balclave || "",
        balimpresora: state.balimpresora || "",
        loccodigo: state.loccodigo || "",
        balstatus: state.balstatus || "A",
      })
    }
  }, [state])

  // Carga de Localidades
  const { data: rawLocalidades, isLoading: isLoadingLoc } = useQuery({
    queryKey: ["listaLocalidadesBalanzas"],
    queryFn: async () => {
      try {
        const response = await api.get("/UbicacionesPPE/getListaLocalidades")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Localidades:", error)
        return []
      }
    },
  })

  const locValidadas = Array.isArray(rawLocalidades)
    ? rawLocalidades
    : Array.isArray(rawLocalidades?.data)
      ? rawLocalidades.data
      : []
  const listaLocalidades = locValidadas.map((item) => ({
    id: item.loccodigo || item.id,
    label: item.label || `${item.loccodigo} - ${item.locdescri || ""}`,
  }))

  const {
    mutateAsync: SaveEdicionBalanza,
    isPending: isSavingEdicionBalanza,
    isError,
  } = useMutation({
    queryKey: ["isEditingBalanza"],
    fn: async (data) => {
      const response = await api.post("/Balanzas/updateBalanzas", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    if (field === "balip" || field === "balclave" || field === "balstatus") {
      setFormData((prev) => ({ ...prev, [field]: value }))
    } else {
      setFormData((prev) => ({ ...prev, [field]: value.toUpperCase() }))
    }
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    const payload = {
      balcodigoNew: formData.balcodigo,
      balcodigoOld: balcodigoViejo,
      baldescri: formData.baldescri,
      balip: formData.balip,
      balclave: formData.balclave,
      balimpresora: formData.balimpresora,
      loccodigo: formData.loccodigo,
      balstatus: formData.balstatus,
    }

    try {
      if (!payload.loccodigo.trim()) return showWarning("Debe seleccionar una Localidad")
      if (!payload.balcodigoNew.trim()) return showWarning("El Código de la Balanza es obligatorio")
      if (!payload.baldescri.trim()) return showWarning("La Descripción de la Balanza es obligatoria")

      await SaveEdicionBalanza(payload)
    } catch (error) {
      console.error("Error al editar la Balanza:", error)
    }
  }

  const grabarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")
  const toolbarActions = grabarAction
    ? [
        {
          label: grabarAction.acccaption,
          key: grabarAction.acccaption,
          icon: getIconComponent(grabarAction.accnameicono, grabarAction.acctipoico),
        },
      ]
    : []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicionBalanza || isLoadingLoc}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Balanza</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicionBalanza || isLoadingLoc} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos de la Balanza
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={listaLocalidades}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    listaLocalidades.find((c) => c.id === formData.loccodigo) ||
                    (formData.loccodigo ? { id: formData.loccodigo, label: `${formData.loccodigo}` } : null)
                  }
                  onChange={(event, newValue) => handleInputChange("loccodigo", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Localidad *"
                      placeholder="Buscar..."
                      InputLabelProps={{ shrink: true }}
                      error={isError && !formData.loccodigo}
                    />
                  )}
                />
              </Grid>

              {/* CAMPO CÓDIGO: AHORA DESHABILITADO POR REQUERIMIENTO */}
              <Grid item xs={12} sm={2}>
                <TextField
                  disabled
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.balcodigo}
                  sx={{ bgcolor: "#f0f0f0" }}
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.baldescri}
                  onChange={(e) => handleInputChange("baldescri", e.target.value)}
                  error={isError && !formData.baldescri}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Dirección IP"
                  value={formData.balip}
                  onChange={(e) => handleInputChange("balip", e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  type="password"
                  label="Clave"
                  value={formData.balclave}
                  onChange={(e) => handleInputChange("balclave", e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Impresora"
                  value={formData.balimpresora}
                  onChange={(e) => handleInputChange("balimpresora", e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.balstatus}
                  onChange={(e) => handleInputChange("balstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarBalanzas
